import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Popup } from '@atlaskit/popup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FilterPickerSelect } from '../SelectComponents/Select';
import { SelectTrigger } from '../SelectComponents/SelectTrigger';
import { getGroupedOptions, isMultiSelectOptions } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function
export const FilterPicker = _ref => {
  let {
    label,
    options,
    status,
    selectedOptions = [],
    isMulti = false,
    isPopupOpen: controlledIsPopupOpen,
    isSearchable = false,
    hasClearSelectionButton = false,
    groupSelectedItems = false,
    onPopupToggle,
    onChange,
    onClearFilter
  } = _ref;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const triggerRef = useRef(null);

  // Managing our own state for the options, to support displaying the selected options at the top of the list
  const [groupedOptions, setGroupedOptions] = useState(getGroupedOptions({
    options,
    selectedOptions,
    addSelectedFirst: groupSelectedItems
  }));

  // Synchronize internal popup open state with controlled prop
  useEffect(() => {
    if (controlledIsPopupOpen !== undefined) {
      setIsPopupOpen(controlledIsPopupOpen);
    }
  }, [controlledIsPopupOpen]);
  useEffect(() => {
    onPopupToggle === null || onPopupToggle === void 0 ? void 0 : onPopupToggle(isPopupOpen);
  }, [isPopupOpen, onPopupToggle]);
  const resetPopupState = useCallback(() => {
    setIsPopupOpen(false);
    setSearchInputValue('');
  }, []);
  const focusOnTriggerButton = useCallback(() => {
    // We need a timeout to apply the focus after other event handlers, otherwise the focus can be lost
    setTimeout(() => {
      var _triggerRef$current;
      return (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.focus();
    });
  }, []);
  const handlePopupClose = useCallback(event => {
    // If the user presses the escape key to close the popup, place focus back onto the trigger button
    if (event.key === 'Escape') {
      focusOnTriggerButton();
    }
    resetPopupState();
  }, [focusOnTriggerButton, resetPopupState]);
  const handlePopupTriggerClick = useCallback(() => {
    // if the popup is being closed, clear the search input value
    if (isPopupOpen) {
      resetPopupState();
    } else {
      // If the popup is being opened, update the `grouped` options
      setGroupedOptions(getGroupedOptions({
        options,
        selectedOptions,
        addSelectedFirst: groupSelectedItems
      }));
      setIsPopupOpen(true);
    }
  }, [isPopupOpen, resetPopupState, options, selectedOptions, groupSelectedItems]);
  const handleChange = useCallback(newOptionOrOptions => {
    if (isMultiSelectOptions(newOptionOrOptions)) {
      onChange(newOptionOrOptions);
    } else {
      onChange(newOptionOrOptions ? [newOptionOrOptions] : []);
      resetPopupState();
    }
  }, [onChange, resetPopupState]);
  const handleClearSelection = useCallback(event => {
    event.preventDefault();
    if ('code' in event && ['Enter', 'NumpadEnter', 'Space'].includes(event.code) || event.nativeEvent instanceof MouseEvent) {
      onClearFilter === null || onClearFilter === void 0 ? void 0 : onClearFilter();
      resetPopupState();

      // When clear selection button is clicked by keyboard, place focus on the trigger button
      if ('code' in event && ['Enter', 'NumpadEnter', 'Space'].includes(event.code)) focusOnTriggerButton();
    }
  }, [focusOnTriggerButton, onClearFilter, resetPopupState]);

  // When single select, place focus on the trigger button when the select is closed by keyboard interaction
  const handleSelectKeyDown = useCallback(event => {
    if (!isMulti && 'code' in event && ['Enter', 'NumpadEnter', 'Space'].includes(event.code)) {
      focusOnTriggerButton();
    }
  }, [focusOnTriggerButton, isMulti]);
  const handleFocus = () => {
    //Only setting the focus to first element when nothing is selected.
    if (!selectedOptions.length) {
      setGroupedOptions(menuOptions => menuOptions.map((option, index) => _objectSpread(_objectSpread({}, option), {}, {
        isFocused: index == 0
      })));
    }
  };
  const handleSearchInputChange = useCallback((newInputValue, meta) => {
    if (meta.action === 'input-change') {
      setSearchInputValue(newInputValue);
    }
  }, []);
  const numberOfSelections = selectedOptions.length;
  return /*#__PURE__*/_jsx(Popup, {
    zIndex: 110,
    isOpen: isPopupOpen,
    onClose: handlePopupClose,
    placement: "bottom-start",
    autoFocus: false,
    trigger: triggerProps => {
      var _selectedOptions$;
      return /*#__PURE__*/_jsx(SelectTrigger, {
        ref: triggerRef,
        triggerProps: triggerProps,
        filterLabel: label,
        firstSelectedOptionLabel: (_selectedOptions$ = selectedOptions[0]) === null || _selectedOptions$ === void 0 ? void 0 : _selectedOptions$.label,
        isSelected: isPopupOpen || numberOfSelections > 0,
        isExpanded: isPopupOpen,
        numberOfSelections: numberOfSelections,
        onClick: handlePopupTriggerClick
      });
    },
    content: () => /*#__PURE__*/_jsx(FilterPickerSelect, {
      testId: "filter-picker.select.".concat(label),
      options: groupedOptions,
      selectedOptions: selectedOptions,
      status: status,
      isMulti: isMulti,
      isSearchable: isSearchable,
      hasClearSelectionButton: hasClearSelectionButton,
      searchInputValue: searchInputValue,
      onSelectionChange: handleChange,
      onClearSelection: handleClearSelection,
      onSearchInputChange: handleSearchInputChange,
      onSelectKeyDown: handleSelectKeyDown,
      onHandleFocus: handleFocus
    })
  });
};